import type { Photo, PhotoState, PhotoType } from '../types';
import { photographersMock } from './photographers.mock';

/**
 * Placeholder imagery uses picsum.photos (deterministic by seed). Step 2 replaces
 * these with real `photovogue-api` S3 image URLs.
 */
const img = (seed: string, w: number, h: number) =>
  `https://picsum.photos/seed/${seed}/${w}/${h}`;

const ASPECT_RATIOS: [number, number][] = [
  [2, 3],
  [3, 2],
  [4, 5],
  [16, 9],
  [1, 1],
];

const STATE_CYCLE: PhotoState[] = [
  'ready',
  'ready',
  'uploaded',
  'approved',
  'approved',
  'to_fix',
  'rejected',
  'banned',
];

const photographersWithPhotos = photographersMock.filter(
  (photographer) => photographer.totalPhotos > 0,
);

const CATEGORIES = [
  'Portrait',
  'Fashion',
  'Still Life',
  'Landscape',
  'Documentary',
  'Street',
  'Conceptual',
  'Nude',
];

const THEMES = [
  'Identity',
  'Nature',
  'Urban',
  'Family',
  'Travel',
  'Beauty',
  'Politics',
  'Memory',
];

const TAGS = [
  'analog',
  'black-and-white',
  'studio',
  'natural-light',
  'portrait',
  'editorial',
  'reportage',
  'minimal',
];

const TITLES = [
  'Untitled',
  'Golden Hour',
  'City Lines',
  'Quiet Room',
  'Salt & Skin',
  'After the Rain',
  'Blue Interval',
  'Northbound',
  'Paper Moon',
  'Field Notes',
  'Low Tide',
  'Second Language',
];

const pick = <T>(arr: T[], i: number): T => arr[i % arr.length]!;

// Dates are relative to "now" so the moderation queue's default last-month
// upload-date filter always surfaces a realistic set of recent photos.
const base = new Date();
base.setHours(12, 0, 0, 0);
const BASE = base.getTime();
const DAY = 24 * 60 * 60 * 1000;

export const photosMock: Photo[] = Array.from({ length: 48 }, (_, i) => {
  const photographer = pick(photographersWithPhotos, i);
  const state = pick(STATE_CYCLE, i);
  const seed = `pv-${i + 1}`;
  const [aspectWidth, aspectHeight] = pick(ASPECT_RATIOS, i);
  const heightFor = (width: number) =>
    Math.round((width * aspectHeight) / aspectWidth);
  const createdMs = BASE - i * DAY;

  const types: PhotoType[] = [];
  if (i % 7 === 0) types.push('best');
  if (i % 11 === 0) types.push('daily');

  return {
    id: `photo-${i + 1}`,
    title: `${pick(TITLES, i)} #${i + 1}`,
    description:
      'Submitted for review. Awaiting an editorial decision on state and curation.',
    state,
    category: pick(CATEGORIES, i),
    tags: Array.from(new Set([pick(TAGS, i), pick(TAGS, i + 3)])),
    themes: [pick(THEMES, i)],
    types,
    crop: null,
    photographer: { id: photographer.id, name: photographer.name },
    imageUrls: {
      thumbnail: img(seed, 300, 300),
      small: img(seed, 500, 500),
      smallFit: img(seed, 500, heightFor(500)),
      medium: img(seed, 900, 900),
      large: img(seed, 1400, 1050),
      gallery: img(seed, 1600, 1200),
      private: img(seed, 1600, heightFor(1600)),
    },
    createdAt: new Date(createdMs).toISOString(),
    stateUpdatedAt:
      state === 'uploaded'
        ? null
        : new Date(createdMs + 6 * 60 * 60 * 1000).toISOString(),
  };
});
